import { Brain } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardMetrics } from "@/components/lender/dashboard/DashboardMetrics";
import { PerformanceChart } from "@/components/lender/dashboard/PerformanceChart";
import { RecentApplications } from "@/components/lender/dashboard/RecentApplications";

const LoadingState = () => (
  <div className="flex items-center justify-center min-h-[400px]">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const LenderDashboard = () => {
  const { toast } = useToast();
  const { data: loanStats, isLoading, error } = useQuery({
    queryKey: ['lenderStats'],
    queryFn: async () => {
      try {
        // First get the lender ID for the current user
        const { data: { user }, error: userError } = await supabase.auth.getUser();
        if (userError) throw userError;
        if (!user) throw new Error('No authenticated user found');

        const { data: lender, error: lenderError } = await supabase
          .from('lenders')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (lenderError) throw lenderError;
        if (!lender) throw new Error('No lender profile found');

        const { data: loans, error: loansError } = await supabase
          .from('loan_applications')
          .select('*')
          .eq('lender_id', lender.id);

        if (loansError) throw loansError;

        return {
          totalLoans: loans?.length || 0,
          activeLoans: loans?.filter(loan => loan.status === 'active').length || 0,
          totalAmount: loans?.reduce((sum, loan) => sum + (loan.amount || 0), 0) || 0,
          averageRiskScore: loans?.reduce((sum, loan) => sum + (loan.risk_score || 0), 0) / (loans?.length || 1) || 0,
        };
      } catch (err) {
        console.error('Error fetching lender stats:', err);
        toast({
          title: "Error",
          description: "Failed to load dashboard data",
          variant: "destructive",
        });
        throw err;
      }
    },
    retry: 1,
    gcTime: 60000,
  });

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] space-y-4">
        <p className="text-red-500">Failed to load dashboard data</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  if (isLoading) {
    return <LoadingState />;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Lender Dashboard</h1>
        <Button variant="outline" className="flex items-center gap-2">
          <Brain className="h-4 w-4" />
          AI Insights
        </Button>
      </div>
      
      {loanStats && <DashboardMetrics stats={loanStats} />}

      <div className="grid md:grid-cols-2 gap-6">
        <PerformanceChart />
        <RecentApplications />
      </div>
    </div>
  );
};

export default LenderDashboard;